import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import type { PolymorphicComponent } from 'react-polymorphed';
import TraderaSymbol from '@tradera/blueprint/theme/logos/tradera_symbol.svg?jsx';
import styles from './item-card-image.module.scss';
import { LazyImageLoader } from 'tradera/components/image-lazy-load/image-lazy-load';
import type { SrcSetMap } from './item-card-image-size';
import { getSrc, getSrcSet } from './item-card-image-size';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { ITEM_CARD_IMAGE_MODES } from './item-card-image-modes';

type ImageLoaderProps = {
    className?: string;
    style?: React.CSSProperties;
    src?: string;
    srcSetMap?: SrcSetMap;
    imageMode?: ITEM_CARD_IMAGE_MODES;
    shortDescription?: string;
    loadingClassName?: string;
    showFallback: boolean;
    children?: React.ReactNode;
};

export const ImageLoader: PolymorphicComponent<'div', ImageLoaderProps> = ({
    as: Component = 'div',
    className,
    style,
    src: srcOriginal,
    srcSetMap,
    imageMode,
    shortDescription = '',
    loadingClassName = '',
    showFallback,
    children,
    ...otherProps
}) => {
    const isOptimizedWebpImagesEnabled = useIsFeatureEnabled(
        'webp-optimized-item-card-images'
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const onLoad = useCallback(() => setIsLoaded(true), [setIsLoaded]);
    const onError = useCallback(() => setHasError(true), [setHasError]);
    const isSquareImageMode = imageMode === ITEM_CARD_IMAGE_MODES.SQUARE;

    return (
        <Component
            title={shortDescription}
            {...otherProps}
            className={classnames(
                styles.wrapper,
                {
                    [loadingClassName]: !isLoaded
                },
                className
            )}
            style={style}>
            {showFallback || hasError || !srcOriginal ? (
                <div
                    className={classnames(
                        styles.fillAspectRatio,
                        styles.itemCardImageFallback
                    )}>
                    <TraderaSymbol
                        width="50%"
                        className={styles.fallbackLogo}
                    />
                </div>
            ) : (
                <picture>
                    {isOptimizedWebpImagesEnabled && isSquareImageMode ? (
                        <source
                            type="image/webp"
                            srcSet={getSrcSet(
                                srcOriginal,
                                '{format}',
                                srcSetMap
                            )}
                        />
                    ) : null}
                    <LazyImageLoader
                        src={
                            isOptimizedWebpImagesEnabled && isSquareImageMode
                                ? getSrc(srcOriginal, '{format}', 'small')
                                : srcOriginal.replace(
                                      '/{format}/',
                                      '/medium-fit/'
                                  )
                        }
                        onLoad={onLoad}
                        onError={onError}
                        className={classnames(
                            styles.fillAspectRatio,
                            styles.primaryImage
                        )}
                    />
                </picture>
            )}
            {isLoaded && children}
        </Component>
    );
};
